import { useState } from "react";
import { Eye, EyeOff, Loader2 } from "lucide-react";
import { ApiError } from "@/lib/api";
import { Button, Dialog, Field, Input } from "@/components/ui/primitives";
import type { ManagedUser } from "./api";
import { useResetPassword, useSetPassword } from "./hooks";

const MIN_LENGTH = 12;

/**
 * Set a specific password for a user (admin manual input), or fall back to
 * generating a random temporary one. Either way, all the user's sessions are
 * signed out server-side.
 */
export function SetPasswordDialog({
  user,
  onClose,
  onGenerated,
}: {
  user: ManagedUser;
  onClose: () => void;
  // Called with the generated temporary password so the parent can show it once.
  onGenerated: (temporaryPassword: string) => void;
}) {
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [mustChange, setMustChange] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const setPw = useSetPassword();
  const reset = useResetPassword();
  const busy = setPw.isPending || reset.isPending;

  const tooShort = password.length > 0 && password.length < MIN_LENGTH;
  const mismatch = confirm.length > 0 && confirm !== password;
  const canSubmit = password.length >= MIN_LENGTH && confirm === password && !busy;

  async function submit() {
    setError(null);
    try {
      await setPw.mutateAsync({
        id: user.id,
        body: { new_password: password, must_change: mustChange },
      });
      onClose();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not set the password.");
    }
  }

  async function generateInstead() {
    setError(null);
    try {
      const res = await reset.mutateAsync(user.id);
      onGenerated(res.temporary_password);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not reset the password.");
    }
  }

  return (
    <Dialog open onClose={onClose} title={`Reset password — ${user.full_name}`}>
      <div className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Type a new password for <span className="font-medium text-foreground">{user.email}</span>.
          They&apos;ll be signed out of any active sessions.
        </p>

        {error && (
          <div
            role="alert"
            className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive"
          >
            {error}
          </div>
        )}

        <Field
          label="New password"
          htmlFor="new_password"
          error={tooShort ? `At least ${MIN_LENGTH} characters.` : undefined}
        >
          <div className="relative">
            <Input
              id="new_password"
              type={show ? "text" : "password"}
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="pr-10"
            />
            <button
              type="button"
              onClick={() => setShow((s) => !s)}
              aria-label={show ? "Hide password" : "Show password"}
              className="absolute right-2 top-1.5 rounded-md p-1 text-muted-foreground hover:bg-accent"
            >
              {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
        </Field>

        <Field
          label="Confirm password"
          htmlFor="confirm_password"
          error={mismatch ? "Passwords do not match." : undefined}
        >
          <Input
            id="confirm_password"
            type={show ? "text" : "password"}
            autoComplete="new-password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
          />
        </Field>

        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={mustChange}
            onChange={(e) => setMustChange(e.target.checked)}
          />
          Require the user to change it at next login
        </label>

        <div className="flex items-center justify-between gap-2 border-t border-border pt-4">
          <button
            type="button"
            onClick={() => void generateInstead()}
            disabled={busy}
            className="text-sm text-muted-foreground hover:text-foreground hover:underline disabled:opacity-50"
          >
            Generate a random one instead
          </button>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={onClose} disabled={busy}>
              Cancel
            </Button>
            <Button onClick={() => void submit()} disabled={!canSubmit}>
              {setPw.isPending && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
              Set password
            </Button>
          </div>
        </div>
      </div>
    </Dialog>
  );
}
